// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TargetFigure from "src/components/containers/shared/ReportSections/ReportGraph/AniseikoniaGraph/TargetFigure";

// Types
import {
  AniseikoniaMeridianKeyType,
  ExamSectionReportType,
  isAniseikoniaLiveStep,
} from "src/services/exams/types";

// Utils
import {
  pct,
  signed,
} from "src/components/containers/shared/ReportSections/ReportGraph/SynoptophoreGraph/shared/formatters";

type AniseikoniaType = {
  examData: ExamSectionReportType;
};

const MERIDIAN_KEYS: Record<string, AniseikoniaMeridianKeyType> = {
  Horizontal: "horizontal",
  Vertical: "vertical",
  Overall: "overall",
};

// Where the control sits on its own travel, as a fraction of the way along.
const travelFraction = (value: number, min: number, max: number) => {
  if (!Number.isFinite(value) || max <= min) return 0.5;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
};

/**
 * The aniseikonia section's live view: what the patient is being asked to match, the two halves at
 * the sizes they are on the screen right now, and whether they are still fusing at all.
 *
 * Deliberately absent: which presentation is a control probe. Two of every six are probes with a
 * known right answer, and a probe the technician can pick out is not a control any more, so
 * settings and probes are drawn identically. `currentStep.kind` is in the payload for consumers
 * that aggregate steps; it must never reach the screen.
 */
const Aniseikonia = ({ examData }: AniseikoniaType) => {
  const { t } = useTranslation();

  const currentStep = examData?.state?.currentStep;
  const step = isAniseikoniaLiveStep(currentStep) ? currentStep : null;

  const geometry = examData?.exam?.targetGeometry;

  const meridian = useMemo(
    () => (step ? MERIDIAN_KEYS[step.meridian] ?? "horizontal" : "horizontal"),
    [step]
  );

  if (!step) {
    return (
      <Box
        sx={{
          width: "100%",
          padding: "8px",
          borderRadius: "4px",
          backgroundColor: "#5B6D7A20",
        }}
      >
        <Typography fontSize="12px" color="#5B6D7A">
          {t("Waiting for the next step to start.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="12px" width="100%">
      {/* What the patient is doing, and where through the section they are. The activity is a
          translation key in its own right, so no sentence is built here out of the meridian and
          the step kind -- which would have to leak the kind to do it. */}
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
      >
        <Typography fontSize="14px" fontWeight={600}>
          {t(step.activity)}
        </Typography>
        {step.stepNumber !== null && step.stepCount !== null && (
          <Typography fontSize="12px" color="#5B6D7A">
            {t("Setting {{ current }} of {{ total }}", {
              current: step.stepNumber,
              total: step.stepCount,
            })}
          </Typography>
        )}
      </Box>

      {/* The one status worth making loud: with the lock gone the section is measuring nothing
          at all, because each eye sees only one half of the target and the pair exists in fusion
          or not. */}
      <Box sx={{ backgroundColor: "#5B6D7A20", borderRadius: "4px", p: "8px" }}>
        <Typography
          fontSize="12px"
          fontWeight={600}
          color={step.lockHeld ? "#22BE39" : "#E10C0C"}
        >
          {step.lockHeld ? t("Lock Held") : t("Lock Lost")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: "10px",
        }}
      >
        {/* What the patient saw, redrawn every tick from `view` -- which already has the
            control's sign and the square-root split applied, so nothing here works back to the
            halves from a percentage. */}
        <Box
          sx={{
            border: "1px solid #5B6D7A40",
            borderRadius: "4px",
            p: "8px",
            minWidth: 0,
          }}
        >
          <Typography
            fontSize="11px"
            color="#5B6D7A"
            sx={{
              textTransform: "uppercase",
              letterSpacing: ".05em",
              mb: "6px",
            }}
          >
            {t("On Screen")}
          </Typography>
          <TargetFigure
            geometry={geometry}
            view={step.view}
            meridian={meridian}
            size={150}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid #5B6D7A40",
            borderRadius: "4px",
            p: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minWidth: 0,
          }}
        >
          <Box>
            <Typography
              fontSize="11px"
              color="#5B6D7A"
              sx={{ textTransform: "uppercase", letterSpacing: ".05em" }}
            >
              {t("Driven Half")}
            </Typography>
            <Typography fontSize="12px">{t(step.drivenEye)}</Typography>
          </Box>

          {/* The running estimate is already an aniseikonia: the device flips the sign, because
              the control and the patient's own difference are opposite and a display that
              flipped it again would be confidently wrong. */}
          <Box>
            <Typography
              fontSize="11px"
              color="#5B6D7A"
              sx={{ textTransform: "uppercase", letterSpacing: ".05em" }}
            >
              {t("Running Estimate")}
            </Typography>
            <Typography fontSize="12px">
              {step.runningEstimatePercent === null
                ? t("N/A")
                : `${signed(step.runningEstimatePercent, 1)}%`}
            </Typography>
          </Box>

          {/* The control's position on its own travel. Drawn as a position on the scale rather
              than as a number the technician might read as a result -- it is where the stick is,
              and it has the opposite sign to the number the report will show. */}
          <Box>
            <Typography
              fontSize="11px"
              color="#5B6D7A"
              sx={{ textTransform: "uppercase", letterSpacing: ".05em" }}
            >
              {t("Current Setting")}
            </Typography>
            <Box
              role="meter"
              aria-label={t("Current Setting")}
              aria-valuenow={step.controlPercent}
              aria-valuemin={step.travelMinPercent}
              aria-valuemax={step.travelMaxPercent}
              sx={{
                mt: "4px",
                height: "6px",
                borderRadius: "3px",
                backgroundColor: "#5B6D7A30",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: `${
                    travelFraction(
                      step.controlPercent,
                      step.travelMinPercent,
                      step.travelMaxPercent
                    ) * 100
                  }%`,
                  backgroundColor: "#3179FE",
                  borderRadius: "3px",
                }}
              />
            </Box>
            <Typography fontSize="11px" color="#5B6D7A" sx={{ mt: "2px" }}>
              {pct(step.travelMinPercent, 0)} – {pct(step.travelMaxPercent, 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box display="flex" flexWrap="wrap" gap="16px">
        <Typography fontSize="11px" color="#5B6D7A">
          {t("Fusion Loss Events")}: {step.fusionLossEvents}
        </Typography>
        <Typography fontSize="11px" color="#5B6D7A">
          {t("Suppression Events")}: {step.suppressionEvents}
        </Typography>
      </Box>
    </Box>
  );
};

export default Aniseikonia;
